import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { chainIdAtom } from '@/state/atoms'
import { DecodedCalldata } from '@/types'
import { ExplorerDataType, getExplorerLink } from '@/utils/getExplorerLink'
import { useAtomValue } from 'jotai'
import { ArrowUpRightIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Trans } from '@lingui/react/macro'
import { Address } from 'viem'
import { dtfContractAliasAtom } from './atoms'
import RawCallPreview from './raw-call-preview'
import TokenRewardPreview from './token-reward-preview'
import {
  SetMandatePreview,
  RemoveFromBasketPreview,
  GrantRolePreview,
  RevokeRolePreview,
  SetFeeRecipientsPreview,
  SetMintFeePreview,
  SetTVLFeePreview,
  SetAuctionLengthPreview,
  SetDustAmountPreview,
  SetVotingDelayPreview,
  SetVotingPeriodPreview,
  SetProposalThresholdPreview,
  UpdateQuorumNumeratorPreview,
  UpdateDelayPreview,
  SetOptimisticParamsPreview,
  SelectorRegistryPreview,
} from './dtf-settings-preview'

const ChangesOverviewComponentMap: Record<
  string,
  React.ComponentType<{
    decodedCalldata: DecodedCalldata
    targetAddress?: Address
  }>
> = {
  removeRewardToken: TokenRewardPreview,
  addRewardToken: TokenRewardPreview,
  // DTF Settings functions
  setMandate: SetMandatePreview,
  removeFromBasket: RemoveFromBasketPreview,
  grantRole: GrantRolePreview,
  revokeRole: RevokeRolePreview,
  setFeeRecipients: SetFeeRecipientsPreview,
  setMintFee: SetMintFeePreview,
  setTVLFee: SetTVLFeePreview,
  setAuctionLength: SetAuctionLengthPreview,
  setDustAmount: SetDustAmountPreview,
  // Governance functions
  setVotingDelay: SetVotingDelayPreview,
  setVotingPeriod: SetVotingPeriodPreview,
  setProposalThreshold: SetProposalThresholdPreview,
  updateQuorumNumerator: UpdateQuorumNumeratorPreview,
  setOptimisticParams: SetOptimisticParamsPreview,
  // Timelock functions
  updateDelay: UpdateDelayPreview,
  // Optimistic selector registry functions
  registerSelectors: SelectorRegistryPreview,
  unregisterSelectors: SelectorRegistryPreview,
}

const TABS = {
  SUMMARY: 'summary',
  RAW: 'raw',
}

const ContractProposalChanges = ({
  decodedCalldatas,
  address,
  contractName,
}: {
  decodedCalldatas: DecodedCalldata[]
  address: Address
  contractName?: string
}) => {
  const chainId = useAtomValue(chainIdAtom)
  const contractAliases = useAtomValue(dtfContractAliasAtom)
  const alias =
    contractName ?? contractAliases?.[address.toLowerCase()] ?? 'Unknown'

  return (
    <Tabs
      defaultValue={TABS.SUMMARY}
      className="flex flex-col rounded-3xl bg-background m-1"
    >
      <div className="mx-4 py-4 flex items-center flex-wrap gap-2 border-b">
        <h1 className="text-xl font-semibold text-primary">{alias}</h1>
        <Link
          target="_blank"
          className="mr-auto"
          to={getExplorerLink(address, chainId, ExplorerDataType.ADDRESS)}
        >
          <Button
            size="icon-rounded"
            className="bg-primary/10 text-primary h-6 w-6 p-0 hover:text-white"
          >
            <ArrowUpRightIcon size={18} strokeWidth={1.5} />
          </Button>
        </Link>

        <TabsList>
          <TabsTrigger value={TABS.SUMMARY}>
            <Trans>Summary</Trans>
          </TabsTrigger>

          <TabsTrigger value={TABS.RAW}>
            <Trans>Raw</Trans>
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent className="m-0" value={TABS.SUMMARY}>
        {decodedCalldatas.map((decodedCalldata: DecodedCalldata, index) => {
          const Component =
            ChangesOverviewComponentMap[decodedCalldata.signature]

          if (!Component) {
            return (
              <div
                className="p-4"
                key={`summary-${decodedCalldata.callData}-${index}`}
              >
                <h4 className="text-primary text-lg font-semibold mb-2">
                  {index + 1}/{decodedCalldatas.length}
                </h4>
                <RawCallPreview call={decodedCalldata} />
              </div>
            )
          }

          return (
            <div
              className="p-2"
              key={`summary-${decodedCalldata.callData}-${index}`}
            >
              <h4 className="text-primary text-lg font-semibold mb-2 px-2 pt-2">
                {index + 1}/{decodedCalldatas.length}
              </h4>
              <Component
                decodedCalldata={decodedCalldata}
                targetAddress={address}
              />
            </div>
          )
        })}
      </TabsContent>
      <TabsContent className="m-0" value={TABS.RAW}>
        {decodedCalldatas.map((call, index) => (
          <div className="p-4" key={`raw-${call.callData}-${index}`}>
            <h4 className="text-primary text-lg font-semibold mb-2">
              {index + 1}/{decodedCalldatas.length}
            </h4>
            <RawCallPreview call={call} />
          </div>
        ))}
      </TabsContent>
    </Tabs>
  )
}

export default ContractProposalChanges
